/* eslint-disable no-restricted-syntax */
/*
 * Project: BookingEngine
 */
import { apiGet, apiPost } from '../api';
import { sendGTMEvents } from './Booking';
import { toUseWithAges } from '../utils/Room';
import { addRoomToCartEvents, clearCartEvent } from '../events/Booking';
import { productClickBestRate } from '../events/Room';
import { shoppingCartToCart } from '../reducers/Cart';
import { setOpenReservationDetails } from './Session';
import { setCoinsApplied } from './Loyalty';
import { checkExtrasByRoom, setExtrasAvailId, setExtrasByRoomSelected } from './Extras';

export const CART_ERRORS = {
  EMPTY_CART: 'EMPTY_CART', // error when emptying the basket
  ADD_ROOM_CHOICES: 'ADD_ROOM_CHOICES', // error when selecting room choices
  ADD_EXTRA_CHOICES: 'ADD_EXTRA_CHOICES', // error when selecting extra choices
  ADD_FREEMODE_ROOM: 'ADD_FREEMODE_ROOM', // error when adding a room to the cart
  REMOVE_FREEMODE_ROOM: 'REMOVE_FREEMODE_ROOM', // error when removing room choices
  REMOVE_FREEMODE_EXTRA: 'REMOVE_FREEMODE_EXTRA', // error when removing extra choices
  MAX_ROOMS_REACHED: 'MAX_ROOMS_REACHED', // limit of rooms in basket reached
  HOTEL_MIX_FORBIDDEN: 'HOTEL_MIX_FORBIDDEN', // mixing hotels is not allowed
  INCORRECT_SETTINGS: 'INCORRECT_SETTINGS', // forced amount prices
  HOTEL_NOT_FOUND: 'HOTEL_NOT_FOUND', // hotel not found / inactive
  OTHER_REASON: 'OTHER_REASON',
};

const sendAddRoomsToCartEvents = (state, apiCart) => (dispatch) => {
  const { lang } = state.i18nState;
  const userCurrency = state.booking.filterCurrency;
  const utmParams = state.bookingFormState.utmParams || {};

  const events = addRoomToCartEvents({
    lang,
    cart: shoppingCartToCart(apiCart), // convert to the front-end structure
    userCurrency,
    utmSource: utmParams.utm_source,
  });
  dispatch(sendGTMEvents(events));
};

/* Sends the signal that the cart is about to be emptied */
const clearCart = () => ({ type: 'CART_CLEAR' });

/**
 *  Actions - exported
 */

/* Sets the error message associated to the last cart action */
export const updateCartErrorCode = (errorCode) => ({ type: 'CART_SET_ERROR_CODE', errorCode });

/* Sets the hotel and room list that is in the cart. This used for classic and free-mode carts */
export const updateCartContent = (updatedCart) => ({ type: 'CART_UPDATE_CONTENT', updatedCart });

export const updateCartContentAndRedirectToConfirmation = (updatedCart, navigate) => (dispatch) => {
  dispatch({ type: 'CART_UPDATE_CONTENT', updatedCart });
  navigate('/confirmation/');
};

/* Sets the cart as being updating */
export const setCartUpdating = (isUpdating) => (dispatch) => {
  dispatch(({ type: 'CART_SET_UPDATING', isUpdating }));
  // When the cart is updating, we clear the message that was being displayed
  if (isUpdating) {
    dispatch(updateCartErrorCode(null));
  }
};

/* Clears the cart from any previous selection */
export const emptyShoppingCart = () => (dispatch, getState) => {
  dispatch(setCartUpdating(true));
  dispatch(emptyShoppingCartEvent());

  const state = getState();
  const { sessionId } = state.bookingFormState;

  dispatch(setCoinsApplied(state.loyalty.memberInfo ? state.loyalty.memberInfo.availableCoins : 0));

  apiGet(`api-pb/shopping-cart/clear/${sessionId}/`).then(() => {
    // The success response has a statusCode of 200 and an empty response body
    dispatch(clearCart());
    dispatch(setCartUpdating(false));
    dispatch(setExtrasAvailId(null));
    dispatch(setExtrasByRoomSelected(null));
  }).catch(() => {
    dispatch(updateCartErrorCode(CART_ERRORS.EMPTY_CART));
  });
};

export const emptyShoppingCartEvent = () => (dispatch, getState) => {
  const state = getState();
  const events = clearCartEvent({
    lang: state.i18nState.lang,
    cart: state.cart,
    userCurrency: state.booking.filterCurrency,
  });
  dispatch(sendGTMEvents(events));
};

export const selectBestRateEvents = (bestValueComb) => (dispatch, getState) => {
  const state = getState();
  const roomEvents = productClickBestRate(state, bestValueComb);
  dispatch(sendGTMEvents(roomEvents));
};

export const addSelectedExtrasToCart = (extras) => (dispatch, getState) => {
  dispatch(setCartUpdating(true));
  const state = getState();
  const { sessionId } = state.bookingFormState;
  return apiPost(`api-pb/shopping-cart/extra/add/${sessionId}/`, extras).then((response) => {
    if (!response || !response.success) {
      dispatch(updateCartErrorCode(CART_ERRORS.ADD_EXTRA_CHOICES));
      dispatch(setCartUpdating(false));
      return;
    }
    // When no error happens, we update the cart and associated rooms
    dispatch(updateCartContent(response.scart));
    dispatch(setCartUpdating(false));
    dispatch(setCoinsApplied(state.loyalty.coinsApplied));
    // Mandamos a llamar a redeemCoins porque usamos los elementos del shopping cart para calcular
    // el loyalty_new_member_discount_to_apply
  });
};

export const addSelectedRoomsToCart = (rooms, navigate) => (dispatch, getState) => {
  // First we empty the cart in the state. The first roomAdd request will replace the existing cart contents
  dispatch(clearCart());
  dispatch(setCartUpdating(true));

  const state = getState();
  const form = state.bookingFormState;
  const { hotel, filterCurrency } = state.booking;
  const bodyRQs = rooms.map((roomSelection) => (
    selectedRoomToCartRQ(hotel, roomSelection, filterCurrency, state.booking.last_requested_info)
  ));
  addNewRoomsToCart(form.sessionId, state.i18nState.lang, bodyRQs).then(async (response) => {
    if (response.hasError) {
      dispatch(updateCartErrorCode(CART_ERRORS.ADD_ROOM_CHOICES));
      dispatch(setCartUpdating(false));
      return;
    }
    // When no error happens, we update the cart and associated rooms
    dispatch(updateCartContent(response.scart));

    // We also need to send GA "addToCart" events.
    dispatch(sendAddRoomsToCartEvents(state, response.scart));

    dispatch(setExtrasAvailId(null));
    dispatch(setExtrasByRoomSelected(null));
    const isLoyalty = !!(state.loyalty.memberInfo && state.loyalty.memberInfo.accountHolder);
    // Send get extra availability synchronous for each room
    // eslint-disable-next-line no-unused-vars
    for (const room of rooms) {
      const {
        roomCode, boardCode, rateCode, use: distribution, roomId,
      } = room;
      // eslint-disable-next-line no-await-in-loop
      await dispatch(checkExtrasByRoom({
        roomCode, boardCode, rateCode, distribution, roomId, isLoyalty,
      }));
      // Para cada cuarto se cargan sus extras
    }

    navigate('/confirmation/');
    dispatch(setOpenReservationDetails(false));
    dispatch(setCartUpdating(false));
  }).catch(() => {
    dispatch(setCartUpdating(false));
  });
};

const addSingleRoomToCart = (sessionId, action, lang, body) => { // eslint-disable-line arrow-body-style
  return apiPost(`api-pb/shopping-cart/room/${action}/${sessionId}/`, body, lang).then((response) => ({
    hasError: response.error_code !== '00',
    scart: response.scart,
    uuids: [response.room_uuid],
  }));
};

const addNewRoomsToCart = (sessionId, lang, bodies) => { // eslint-disable-line arrow-body-style
  return addSingleRoomToCart(sessionId, 'replace', lang, bodies[0]).then((firstRS) => {
    // We return the single response when there is only 1 room, or the first response failed
    if (bodies.length === 1 || firstRS.hasError) {
      return firstRS;
    }
    // Add the second room and return the global response
    return addSingleRoomToCart(sessionId, 'add', lang, bodies[1]).then((secondRS) => { // eslint-disable-line arrow-body-style
      if (bodies.length === 2 || secondRS.hasError) {
        return {
          hasError: secondRS.hasError,
          scart: secondRS.scart,
          uuids: [firstRS.uuids[0], secondRS.uuids[0]],
        };
      }
      // Add the third room and return the global response
      return addSingleRoomToCart(sessionId, 'add', lang, bodies[2]).then((thirdRS) => { // eslint-disable-line arrow-body-style
        if (bodies.length === 3 || thirdRS.hasError) {
          return {
            hasError: thirdRS.hasError,
            scart: thirdRS.scart,
            uuids: [firstRS.uuids[0], secondRS.uuids[0], thirdRS.uuids[0]],
          };
        }
        // Add the fourth room and return the global response
        return addSingleRoomToCart(sessionId, 'add', lang, bodies[3]).then((fourthRS) => { // eslint-disable-line arrow-body-style
          if (bodies.length === 4 || fourthRS.hasError) {
            return {
              hasError: fourthRS.hasError,
              scart: fourthRS.scart,
              uuids: [firstRS.uuids[0], secondRS.uuids[0], thirdRS.uuids[0], fourthRS.uuids[0]],
            };
          }
          return {};
        });
      });
    });
  });
};

/**
 *  Transformation functions to adapt to the API format
 */

const selectedRoomToCartRQ = (hotel, roomSelection, userCurrency, lastAvail) => ({
  code: hotel.code,
  // we send the user currency as it can determine the currency for the available payment methods
  user_currency: userCurrency.code,
  room: {
    use: toUseWithAges(hotel, roomSelection.use),
    checkin: roomSelection.checkin ? roomSelection.checkin : lastAvail.checkin,
    checkout: roomSelection.checkout ? roomSelection.checkout : lastAvail.checkout,
    room: { code: roomSelection.roomCode },
    rate: { code: roomSelection.rateCode },
    board: { code: roomSelection.boardCode },
    avail_id: roomSelection.availId,
  },
});
